import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Slide,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  Delete,
  Edit,
  Print,
  Category as CategoryIcon,
  ShoppingCart,
  AttachMoney,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BulkActionsToolbar = ({
  selectedItems,
  onDeselectAll,
  categories,
  sales,
  onBulkUpdate,
  onBulkDelete,
  onPrintLabels,
}) => {
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSale, setSelectedSale] = useState('');
  const [priceAdjustmentType, setPriceAdjustmentType] = useState('percentage');
  const [priceAdjustmentValue, setPriceAdjustmentValue] = useState('');

  if (selectedItems.length === 0) {
    return null;
  }

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;
    await onBulkUpdate({ status: selectedStatus });
    setStatusDialogOpen(false);
    setSelectedStatus('');
  };

  const handleCategoryUpdate = async () => {
    if (!selectedCategory) return;
    await onBulkUpdate({ category_id: parseInt(selectedCategory) });
    setCategoryDialogOpen(false);
    setSelectedCategory('');
  };

  const handleSaleUpdate = async () => {
    if (!selectedSale) return;
    await onBulkUpdate({ sale_id: parseInt(selectedSale) });
    setSaleDialogOpen(false);
    setSelectedSale('');
  };

  const handlePriceUpdate = async () => {
    if (!priceAdjustmentValue) return;
    await onBulkUpdate({
      price_adjustment: parseFloat(priceAdjustmentValue),
      price_adjustment_type: priceAdjustmentType,
    });
    setPriceDialogOpen(false);
    setPriceAdjustmentValue('');
    setPriceAdjustmentType('percentage');
  };

  const handleDelete = async () => {
    await onBulkDelete();
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          px: 3,
          py: 2,
          minWidth: 600,
          maxWidth: '90%',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" fontWeight="bold">
              {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={onDeselectAll}
              startIcon={<Close />}
            >
              Clear Selection
            </Button>
          </Box>

          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={() => setStatusDialogOpen(true)}
            >
              Change Status
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CategoryIcon />}
              onClick={() => setCategoryDialogOpen(true)}
            >
              Change Category
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachMoney />}
              onClick={() => setPriceDialogOpen(true)}
            >
              Adjust Price
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ShoppingCart />}
              onClick={() => setSaleDialogOpen(true)}
            >
              Assign to Sale
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Print />}
              onClick={onPrintLabels}
            >
              Print Labels
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </Box>

          <IconButton size="small" onClick={onDeselectAll}>
            <Close />
          </IconButton>
        </Box>
      </Paper>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Status for {selectedItems.length} Items</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={selectedStatus}
              label="New Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="reserved">Reserved</MenuItem>
              <MenuItem value="sold">Sold</MenuItem>
              <MenuItem value="damaged">Damaged</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            disabled={!selectedStatus}
            startIcon={<CheckCircle />}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Change Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Category for {selectedItems.length} Items</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Category</InputLabel>
            <Select
              value={selectedCategory}
              label="New Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCategoryUpdate}
            disabled={!selectedCategory}
            startIcon={<CheckCircle />}
          >
            Update Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sale Assignment Dialog */}
      <Dialog
        open={saleDialogOpen}
        onClose={() => setSaleDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign {selectedItems.length} Items to Sale</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Sale</InputLabel>
            <Select
              value={selectedSale}
              label="Sale"
              onChange={(e) => setSelectedSale(e.target.value)}
            >
              {sales.map((sale) => (
                <MenuItem key={sale.id} value={sale.id}>
                  {sale.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaleUpdate}
            disabled={!selectedSale}
            startIcon={<CheckCircle />}
          >
            Assign to Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price Adjustment Dialog */}
      <Dialog
        open={priceDialogOpen}
        onClose={() => setPriceDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Price for {selectedItems.length} Items</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
            <FormLabel component="legend">Adjustment Type</FormLabel>
            <RadioGroup
              value={priceAdjustmentType}
              onChange={(e) => setPriceAdjustmentType(e.target.value)}
            >
              <FormControlLabel
                value="percentage"
                control={<Radio />}
                label="Percentage Change"
              />
              <FormControlLabel
                value="increase"
                control={<Radio />}
                label="Increase by Amount"
              />
              <FormControlLabel
                value="decrease"
                control={<Radio />}
                label="Decrease by Amount"
              />
              <FormControlLabel value="fixed" control={<Radio />} label="Set Fixed Price" />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            label={
              priceAdjustmentType === 'percentage'
                ? 'Percentage'
                : priceAdjustmentType === 'fixed'
                ? 'Fixed Price'
                : 'Amount'
            }
            type="number"
            value={priceAdjustmentValue}
            onChange={(e) => setPriceAdjustmentValue(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment:
                priceAdjustmentType !== 'percentage' ? (
                  <InputAdornment position="start">$</InputAdornment>
                ) : null,
              endAdornment:
                priceAdjustmentType === 'percentage' ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : null,
            }}
            helperText={
              priceAdjustmentType === 'percentage'
                ? 'Use positive value to increase, negative to decrease'
                : priceAdjustmentType === 'fixed'
                ? 'All selected items will be set to this price'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePriceUpdate}
            disabled={!priceAdjustmentValue}
            startIcon={<CheckCircle />}
          >
            Adjust Prices
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete {selectedItems.length} Items?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedItems.length} selected item
            {selectedItems.length !== 1 ? 's' : ''}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            startIcon={<Delete />}
          >
            Delete Items
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkActionsToolbar;
